<script setup>
// МегаФон ВАТС telephony inbox settings: connection diagnostics, webhook
// checklist (the ВАТС has no API to register notifications — the operator pastes
// the CRM address + token into the cabinet and confirms here), and agent ↔ ВАТС
// employee/SIP-line mapping.
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import MegapbxAPI from 'dashboard/api/megapbx';
import NextButton from 'dashboard/components-next/button/Button.vue';
import AudioModeToggle from 'dashboard/components-next/call/AudioModeToggle.vue';

const props = defineProps({
  inbox: { type: Object, required: true },
});

const { t } = useI18n();

const checks = ref([]);
const isChecking = ref(false);
const webhookUrl = ref('');
const crmToken = ref('');
const webhookSeen = ref({});
const employees = ref([]);
const agents = ref([]);

const WEBHOOK_EVENTS = [
  { key: 'event', required: true },
  { key: 'history', required: true },
  { key: 'contact', required: false },
  { key: 'rating', required: false },
];

const inboxId = computed(() => props.inbox.id);

const noWebhooksYet = computed(
  () => Object.keys(webhookSeen.value).length === 0
);

const mappedCount = computed(() => agents.value.filter(a => a.login).length);

const dotClass = status => {
  if (status === 'ok') return 'bg-n-teal-9';
  if (status === 'error') return 'bg-n-ruby-9';
  return 'bg-n-amber-9';
};

const seenLabel = key => {
  const ts = webhookSeen.value[key];
  if (!ts) return t('INBOX_MGMT.MEGAPBX.WEBHOOKS.NOT_SEEN');
  const minutes = Math.round((Date.now() / 1000 - ts) / 60);
  return t('INBOX_MGMT.MEGAPBX.WEBHOOKS.SEEN', { minutes });
};

const initials = name =>
  (name || '?')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map(w => w[0])
    .join('')
    .toUpperCase();

const decorate = agent => ({
  ...agent,
  sip_password: '',
  editing: !agent.login,
  saving: false,
});

const loadAll = async () => {
  try {
    const [statusRes, agentsRes] = await Promise.all([
      MegapbxAPI.webhookStatus(inboxId.value),
      MegapbxAPI.agents(inboxId.value),
    ]);
    webhookUrl.value = statusRes.data.webhook_url;
    crmToken.value = statusRes.data.crm_token;
    webhookSeen.value = statusRes.data.events || {};
    agents.value = (agentsRes.data.agents || []).map(decorate);
  } catch (error) {
    useAlert(t('INBOX_MGMT.MEGAPBX.LOAD_ERROR'));
  }
};

const loadDirectory = async () => {
  try {
    const { data } = await MegapbxAPI.directory(inboxId.value);
    employees.value = data.employees || [];
    autoMatch();
  } catch (error) {
    useAlert(t('INBOX_MGMT.MEGAPBX.DIRECTORY_ERROR'));
  }
};

// The ВАТС employee login is the reliable bridge to a HelpDesk user via email.
const autoMatch = () => {
  agents.value.forEach(agent => {
    if (agent.login) return;
    const match = employees.value.find(
      e =>
        e.email &&
        agent.email &&
        e.email.toLowerCase() === agent.email.toLowerCase()
    );
    if (!match) return;
    agent.login = match.login;
    agent.ext = match.ext;
  });
};

const runDiagnostics = async () => {
  isChecking.value = true;
  try {
    const { data } = await MegapbxAPI.diagnostics(inboxId.value);
    checks.value = data.checks || [];
  } catch (error) {
    useAlert(t('INBOX_MGMT.MEGAPBX.DIAGNOSTICS_ERROR'));
  } finally {
    isChecking.value = false;
  }
};

const refreshWebhookStatus = async () => {
  try {
    const { data } = await MegapbxAPI.webhookStatus(inboxId.value);
    webhookSeen.value = data.events || {};
  } catch (error) {
    useAlert(t('INBOX_MGMT.MEGAPBX.LOAD_ERROR'));
  }
};

const onEmployeeChange = (agent, login) => {
  const employee = employees.value.find(e => String(e.login) === String(login));
  agent.login = login;
  if (employee) agent.ext = employee.ext;
};

const saveAgent = async agent => {
  if (!agent.login) {
    useAlert(t('INBOX_MGMT.MEGAPBX.AGENTS.PICK_EMPLOYEE'));
    return;
  }
  agent.saving = true;
  try {
    await MegapbxAPI.updateAgent(inboxId.value, {
      user_id: agent.user_id,
      login: agent.login,
      ext: agent.ext,
      sip_login: agent.sip_login,
      sip_password: agent.sip_password || undefined,
    });
    if (agent.sip_password) agent.has_sip_password = true;
    agent.sip_password = '';
    agent.editing = false;
    useAlert(t('INBOX_MGMT.MEGAPBX.AGENTS.SAVED'));
  } catch (error) {
    useAlert(t('INBOX_MGMT.MEGAPBX.AGENTS.SAVE_ERROR'));
  } finally {
    agent.saving = false;
  }
};

const disconnectAgent = async agent => {
  try {
    await MegapbxAPI.deleteAgent(inboxId.value, agent.user_id);
    agent.login = null;
    agent.ext = null;
    agent.sip_login = '';
    agent.sip_password = '';
    agent.has_sip_password = false;
    agent.editing = true;
    useAlert(t('INBOX_MGMT.MEGAPBX.AGENTS.DISCONNECTED'));
  } catch (error) {
    useAlert(t('INBOX_MGMT.MEGAPBX.AGENTS.SAVE_ERROR'));
  }
};

const employeeName = agent => {
  const employee = employees.value.find(
    e => String(e.login) === String(agent.login)
  );
  return employee ? employee.name : '';
};

const copyWebhookUrl = () => {
  navigator.clipboard?.writeText(webhookUrl.value);
  useAlert(t('INBOX_MGMT.MEGAPBX.WEBHOOKS.COPIED'));
};

const copyCrmToken = () => {
  navigator.clipboard?.writeText(crmToken.value);
  useAlert(t('INBOX_MGMT.MEGAPBX.WEBHOOKS.COPIED'));
};

onMounted(async () => {
  await loadAll();
  await loadDirectory();
});
</script>

<template>
  <div class="flex flex-col gap-6 py-6">
    <!-- Где звук: софтфон в браузере или устройство оператора -->
    <AudioModeToggle :inbox="inbox" />

    <!-- Connection -->
    <section class="p-5 border border-n-weak rounded-xl bg-n-solid-1">
      <div class="flex items-start justify-between gap-4">
        <div>
          <h3 class="text-sm font-semibold text-n-slate-12">
            {{ $t('INBOX_MGMT.MEGAPBX.DIAGNOSTICS.TITLE') }}
          </h3>
          <p class="mt-1 text-sm text-n-slate-11">
            {{ $t('INBOX_MGMT.MEGAPBX.DIAGNOSTICS.DESC') }}
          </p>
        </div>
        <NextButton
          type="button"
          faded
          blue
          sm
          class="flex-shrink-0"
          :is-loading="isChecking"
          :label="$t('INBOX_MGMT.MEGAPBX.DIAGNOSTICS.RUN')"
          @click="runDiagnostics"
        />
      </div>
      <ul
        v-if="checks.length"
        class="flex flex-col gap-2 m-0 mt-4 list-none pl-0"
      >
        <li
          v-for="check in checks"
          :key="check.key"
          class="flex items-center gap-3 text-sm"
        >
          <span
            class="size-2 rounded-full flex-shrink-0"
            :class="dotClass(check.status)"
          />
          <span class="text-n-slate-12">
            {{ $t(`INBOX_MGMT.MEGAPBX.CHECKS.${check.key.toUpperCase()}`) }}
          </span>
          <span class="ml-auto text-xs text-n-slate-11">{{
            check.message
          }}</span>
        </li>
      </ul>
    </section>

    <!-- Webhooks -->
    <section class="p-5 border border-n-weak rounded-xl bg-n-solid-1">
      <div class="flex items-start justify-between gap-4">
        <div>
          <h3 class="text-sm font-semibold text-n-slate-12">
            {{ $t('INBOX_MGMT.MEGAPBX.WEBHOOKS.TITLE') }}
          </h3>
          <p class="mt-1 text-sm text-n-slate-11 max-w-2xl">
            {{ $t('INBOX_MGMT.MEGAPBX.WEBHOOKS.DESC') }}
          </p>
        </div>
        <NextButton
          type="button"
          faded
          slate
          sm
          class="flex-shrink-0"
          :label="$t('INBOX_MGMT.MEGAPBX.WEBHOOKS.REFRESH')"
          @click="refreshWebhookStatus"
        />
      </div>

      <div
        v-if="noWebhooksYet"
        class="flex gap-3 p-3 mt-4 rounded-lg bg-n-amber-3 dark:bg-n-amber-2"
      >
        <span class="size-2 mt-1.5 rounded-full bg-n-amber-9 flex-shrink-0" />
        <p class="text-sm text-n-amber-12 leading-relaxed">
          {{ $t('INBOX_MGMT.MEGAPBX.WEBHOOKS.NONE_YET') }}
        </p>
      </div>

      <label class="block mt-4 mb-1 text-xs text-n-slate-11">
        {{ $t('INBOX_MGMT.MEGAPBX.WEBHOOKS.URL_LABEL') }}
      </label>
      <div class="flex items-center gap-2">
        <code
          class="flex-1 px-3 py-2 text-xs rounded-lg bg-n-alpha-2 text-n-slate-12 font-mono truncate"
        >
          {{ webhookUrl }}
        </code>
        <NextButton
          type="button"
          faded
          blue
          sm
          class="flex-shrink-0"
          :label="$t('INBOX_MGMT.MEGAPBX.WEBHOOKS.COPY')"
          @click="copyWebhookUrl"
        />
      </div>

      <label class="block mt-3 mb-1 text-xs text-n-slate-11">
        {{ $t('INBOX_MGMT.MEGAPBX.WEBHOOKS.TOKEN_LABEL') }}
      </label>
      <div class="flex items-center gap-2">
        <code
          class="flex-1 px-3 py-2 text-xs rounded-lg bg-n-alpha-2 text-n-slate-12 font-mono truncate"
        >
          {{ crmToken }}
        </code>
        <NextButton
          type="button"
          faded
          blue
          sm
          class="flex-shrink-0"
          :label="$t('INBOX_MGMT.MEGAPBX.WEBHOOKS.COPY')"
          @click="copyCrmToken"
        />
      </div>

      <ul class="flex flex-col m-0 mt-3 list-none pl-0 divide-y divide-n-weak">
        <li
          v-for="event in WEBHOOK_EVENTS"
          :key="event.key"
          class="flex items-center gap-3 py-2 text-sm"
        >
          <span
            class="size-2 rounded-full flex-shrink-0"
            :class="webhookSeen[event.key] ? 'bg-n-teal-9' : 'bg-n-slate-6'"
          />
          <span class="text-n-slate-12">
            {{
              $t(
                `INBOX_MGMT.MEGAPBX.WEBHOOKS.EVENTS.${event.key.toUpperCase()}`
              )
            }}
          </span>
          <span
            v-if="event.required"
            class="px-1.5 py-0.5 text-[10px] font-medium rounded bg-n-ruby-3 text-n-ruby-11"
          >
            {{ $t('INBOX_MGMT.MEGAPBX.WEBHOOKS.REQUIRED') }}
          </span>
          <span class="ml-auto text-xs text-n-slate-11 whitespace-nowrap">
            {{ seenLabel(event.key) }}
          </span>
        </li>
      </ul>
    </section>

    <!-- Agent mapping -->
    <section class="p-5 border border-n-weak rounded-xl bg-n-solid-1">
      <div class="flex items-start justify-between gap-4">
        <div>
          <h3 class="text-sm font-semibold text-n-slate-12">
            {{ $t('INBOX_MGMT.MEGAPBX.AGENTS.TITLE') }}
          </h3>
          <p class="mt-1 text-sm text-n-slate-11 max-w-2xl">
            {{ $t('INBOX_MGMT.MEGAPBX.AGENTS.DESC') }}
          </p>
        </div>
        <span class="flex-shrink-0 text-xs text-n-slate-11 whitespace-nowrap">
          {{ mappedCount }}/{{ agents.length }}
        </span>
      </div>

      <div class="flex flex-col gap-2 mt-4">
        <div
          v-for="agent in agents"
          :key="agent.user_id"
          class="p-4 rounded-lg bg-n-alpha-1"
        >
          <div class="flex items-center gap-3">
            <span
              class="grid flex-shrink-0 rounded-full size-8 place-items-center bg-n-alpha-2 text-n-slate-11 text-xs font-semibold"
            >
              {{ initials(agent.name) }}
            </span>
            <div class="min-w-0">
              <p class="text-sm font-medium text-n-slate-12 truncate">
                {{ agent.name }}
              </p>
              <p class="text-xs text-n-slate-11 truncate">{{ agent.email }}</p>
            </div>

            <template v-if="!agent.editing">
              <div class="flex items-center gap-2 ml-auto">
                <span
                  class="hidden sm:inline text-xs text-n-slate-11 truncate max-w-32"
                >
                  {{ employeeName(agent) }}
                </span>
                <span
                  class="px-2 py-1 text-xs rounded-md bg-n-teal-3 text-n-teal-11 font-mono whitespace-nowrap"
                >
                  {{ agent.login }}
                </span>
                <span
                  v-if="agent.has_sip_password"
                  class="px-2 py-1 text-xs rounded-md bg-n-alpha-2 text-n-slate-11 whitespace-nowrap"
                >
                  {{ $t('INBOX_MGMT.MEGAPBX.AGENTS.SOFTPHONE_READY') }}
                </span>
                <NextButton
                  type="button"
                  faded
                  slate
                  sm
                  :label="$t('INBOX_MGMT.MEGAPBX.AGENTS.EDIT')"
                  @click="agent.editing = true"
                />
                <NextButton
                  type="button"
                  faded
                  ruby
                  sm
                  :label="$t('INBOX_MGMT.MEGAPBX.AGENTS.DISCONNECT')"
                  @click="disconnectAgent(agent)"
                />
              </div>
            </template>
          </div>

          <div v-if="agent.editing" class="mt-4">
            <div class="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <label class="mb-0 text-xs text-n-slate-11">
                {{ $t('INBOX_MGMT.MEGAPBX.AGENTS.EMPLOYEE') }}
                <select
                  :value="agent.login"
                  class="mt-1 mb-0"
                  @change="onEmployeeChange(agent, $event.target.value)"
                >
                  <option value="">—</option>
                  <option
                    v-for="e in employees"
                    :key="e.login"
                    :value="e.login"
                  >
                    {{ e.name }}{{ e.ext ? ` (${e.ext})` : '' }}
                  </option>
                </select>
              </label>
              <label class="mb-0 text-xs text-n-slate-11">
                {{ $t('INBOX_MGMT.MEGAPBX.AGENTS.SIP_LOGIN') }}
                <input
                  v-model="agent.sip_login"
                  type="text"
                  class="mt-1 mb-0"
                  placeholder="spb@vats704651.megapbx.ru"
                />
              </label>
              <label class="mb-0 text-xs text-n-slate-11">
                {{ $t('INBOX_MGMT.MEGAPBX.AGENTS.SIP_PASSWORD') }}
                <input
                  v-model="agent.sip_password"
                  type="password"
                  class="mt-1 mb-0"
                  :placeholder="
                    agent.has_sip_password
                      ? $t('INBOX_MGMT.MEGAPBX.AGENTS.PASSWORD_SET')
                      : ''
                  "
                />
              </label>
            </div>
            <p class="mt-2 text-xs text-n-slate-11">
              {{ $t('INBOX_MGMT.MEGAPBX.AGENTS.SIP_HINT') }}
            </p>
            <div class="flex flex-wrap items-center gap-3 mt-3">
              <NextButton
                type="button"
                solid
                blue
                sm
                :is-loading="agent.saving"
                :label="$t('INBOX_MGMT.MEGAPBX.AGENTS.SAVE')"
                @click="saveAgent(agent)"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>
